import {
  ChangeEvent,
  KeyboardEvent,
  ReactNode,
  Ref,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Input } from '@mantine/core';
import clsx from 'clsx';

import { ClearButton } from '../clearbutton/ClearButton';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';

import { PeriodValue } from './periodRange';

import calendarStyles from '../datepicker/calendar.module.scss';
import inputStyles from '../input/input.module.scss';
import styles from './period-picker.module.scss';

type PeriodRangeInputProps = {
  id?: string;
  'data-testid': string;
  value?: PeriodValue;
  format: (value: PeriodValue) => string;
  parse: (input: string) => PeriodValue | undefined;
  validate: (value: PeriodValue) => boolean;
  focused: boolean;
  disabled: boolean;
  loading: boolean;
  readOnly: boolean;
  clearable: boolean;
  icon: string;
  /** Stretch to the container instead of the fixed range-input width. */
  fullWidth?: boolean;
  placeholder?: string;
  externalError?: boolean;
  resetVersion: number;
  onClear: () => void;
  onFocus: () => void;
  onValidChange: (value: PeriodValue) => void;
  onValidCommit: () => void;
  ref?: Ref<HTMLInputElement>;
};

export function PeriodRangeInput({
  value,
  format,
  parse,
  validate,
  focused,
  disabled,
  loading,
  readOnly,
  clearable,
  icon,
  fullWidth = false,
  externalError,
  resetVersion,
  onClear,
  onFocus,
  onValidChange,
  onValidCommit,
  ref,
  ...inputProps
}: PeriodRangeInputProps) {
  const formattedValue = value ? format(value) : '';
  const [inputValue, setInputValue] = useState(formattedValue);
  const edited = useRef(false);
  const previousResetVersion = useRef(resetVersion);
  const showClearAction =
    clearable && Boolean(inputValue) && !disabled && !loading && !readOnly;

  useEffect(() => {
    const shouldReset = previousResetVersion.current !== resetVersion;
    if (shouldReset) {
      edited.current = false;
    }
    if (!edited.current) {
      setInputValue(formattedValue);
    }
    previousResetVersion.current = resetVersion;
  }, [formattedValue, resetVersion]);

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    const nextInput = event.currentTarget.value;
    edited.current = true;
    setInputValue(nextInput);

    if (nextInput.trim() === '') {
      if (clearable) {
        onClear();
      }
      return;
    }

    const parsed = parse(nextInput);
    if (parsed != null && validate(parsed)) {
      onValidChange(parsed);
    }
  }

  function revertInput() {
    setInputValue(formattedValue);
    edited.current = false;
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') {
      return;
    }

    const parsed = parse(inputValue);
    if (parsed && validate(parsed)) {
      setInputValue(format(parsed));
      edited.current = false;
      onValidCommit();
      return;
    }

    revertInput();
  }

  let rightSection: ReactNode;
  if (loading) {
    rightSection = <LoadingIndicator />;
  } else if (clearable) {
    rightSection = (
      <ClearButton
        hidden={!showClearAction}
        disabled={disabled}
        onClear={() => {
          edited.current = false;
          setInputValue('');
          onClear();
        }}
      />
    );
  }

  return (
    <Input
      {...inputProps}
      autoComplete="off"
      unstyled
      ref={ref}
      disabled={disabled || loading}
      readOnly={readOnly}
      error={externalError}
      aria-invalid={externalError || undefined}
      classNames={{
        input: clsx(inputStyles['input'], {
          [inputStyles['input--is-focussed']]: focused,
          [inputStyles['input--error']]: externalError,
          [inputStyles['input--is-loading']]: loading,
        }),
        section: inputStyles['section'],
        wrapper: clsx(
          inputStyles['wrapper'],
          fullWidth ? null : styles['input-wrapper']
        ),
      }}
      leftSection={
        icon ? (
          <GoogleFontIcon
            name={icon}
            solid
            className={clsx(calendarStyles['icon'], {
              [calendarStyles['icon--focused']]: focused,
              [calendarStyles['icon--disabled']]: disabled || readOnly,
              [calendarStyles['icon--loading']]: loading,
            })}
          />
        ) : undefined
      }
      rightSection={rightSection}
      type="text"
      value={inputValue}
      onChange={handleInputChange}
      onFocus={onFocus}
      onBlur={revertInput}
      onKeyDown={handleKeyDown}
    />
  );
}
